import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type Id } from '@/infra/id'
import { type Document } from './Document'
import { type DocumentStore } from './DocumentStore'

/**
 * What the document management table's actions act with.
 */
export interface DocumentManagementActionContext {
  documentStore: DocumentStore
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  /**
   * The selected document long ids. Acted-on documents are deselected.
   */
  selection: Set<Id>
  /**
   * Asks the user to confirm, then deletes the documents, resolving with
   * the ids of the deleted ones.
   */
  promptDelete: (documents: Document[], signal?: AbortSignal) => Promise<Id[]>
}

/**
 * Downloads the downloadable documents (latest version only), zipped when
 * there is more than one.
 */
export async function downloadManagedDocuments(
  context: DocumentManagementActionContext,
  candidates: readonly Document[],
) {
  const documents = candidates.filter((doc) => doc.downloadable)

  if (documents.length < 1) {
    context
      .newAlert()
      .withState('neutral')
      .saying(
        'Not a valid selection.',
        'Select one or more documents to download.',
      )
      .autoDismiss(2500)
      .show()

    return
  }

  try {
    if (documents.length === 1) {
      await context.documentStore.download({
        document: documents[0],
        preview: false,
      })
    } else {
      await context.documentStore.downloadAll(documents.map((doc) => doc.id))
    }
  } catch (err) {
    context.createAlertErrorHandler('Downloading failed')(err)
  }

  context
    .newAlert()
    .withState('success')
    .saying('Success!', 'Your download has started!')
    .autoDismiss(2500)
    .show()

  documents.forEach((doc) => context.selection.delete(doc.id))
}

/**
 * Deletes the documents that aren't archived yet, after confirmation.
 */
export async function archiveManagedDocuments(
  context: DocumentManagementActionContext,
  candidates: readonly Document[],
  signal?: AbortSignal,
) {
  const documents = candidates.filter((doc) => !doc.archived)

  if (documents.length < 1) {
    context
      .newAlert()
      .withState('neutral')
      .saying(
        'Not a valid selection.',
        'Select one or more documents that are not currently archived.',
      )
      .autoDismiss(2500)
      .show()

    return
  }

  const deletedIds = await context.promptDelete(documents, signal)
  deletedIds.forEach((id) => context.selection.delete(id))
}
